import dayjs from 'dayjs';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'src/AppContext';
import Button from 'src/components/Button';
import { useDatePicker } from 'src/components/CustomDatePicker';
import { useSelectBox } from 'src/components/SelectBox';
import { ACCOUNT_COUNTRY_VALIDATE_ERROR } from 'src/constants/validate';
import useCustomHistory from 'src/hooks/useCustomHistory';
import { useModal } from 'src/ModalContext';
import { getCountryList, userGeoIp } from 'src/services/api';

export default function AccountAge({ isShow }: AccountProcessProps): JSX.Element {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useCustomHistory();
  const birthInput = useDatePicker();
  const { showModal, errorModal } = useModal();
  const [countries, setCountries] = useState([] as CountryItem[]);
  const countrySelectBox = useSelectBox({
    name: 'country',
    placeholder: t('account/age/country_validate_required_error'),
    validate: {
      required: (value: string) => !!value,
    },
    list: countries,
    component: function component(data) {
      return (
        <option key={data.code} value={data.code}>
          {data.emoji} {data.name}
        </option>
      );
    },
    errors: ACCOUNT_COUNTRY_VALIDATE_ERROR,
  });

  const onClick = useCallback(async () => {
    const countryData = countries.find(({ code }) => code === countrySelectBox.value);
    if (countryData && countryData.ageLimit <= dayjs().diff(birthInput.value, 'year')) {
      history.push('/account/agree');
    } else {
      showModal({
        title: '',
        message: t('account/age/child_alert', {
          country: countryData?.name,
        }),
        success: () => {
          return;
        },
      });
    }
  }, [history, countrySelectBox.value, birthInput, countries]);

  useLayoutEffect(() => {
    dispatch({ type: 'SET_GNB_LEFT', name: 'back' });
    dispatch({ type: 'SET_GNB_RIGHT', name: 'hidden' });
    return () => {
      dispatch({ type: 'RESET_GNB_LEFT' });
      dispatch({ type: 'RESET_GNB_RIGHT' });
    };
  }, []);

  useEffect(() => {
    getCountryList()
      .then((res1) => {
        setCountries(res1.countries);
        userGeoIp().then((res) => {
          const countryCode = res.country;
          const result = res1.countries.find(({ code }) => countryCode.toUpperCase() === code.toUpperCase());
          countrySelectBox.setValue(result ? countryCode : '');
        });
      })
      .catch(errorModal);
  }, []);

  return (
    <div className={[isShow ? '' : 'hidden'].join(' ')}>
      <div className="container mt-6">
        <h1 className="N_20_R">{t('account/age/title')}</h1>
        <div className="mt-4 N_14_R text-brown-grey">{t('account/age/subtitle')}</div>
      </div>
      <div className="mt-6 h-1.5 w-full bg-light-gray-1"></div>
      <div className="container">
        <div className="mt-6 N_12_R text-greyish-brown">{t('account/age/country_title')}</div>
        <div className="mt-1.5">{countrySelectBox.CustomSelectBox}</div>
        <div className="mt-6 N_12_R text-greyish-brown">{t('account/age/birth_title')}</div>
        <div className="mt-1.5">{birthInput.CustomDatePicker}</div>
        <input type="hidden" name="birth" value={birthInput.value} />
        <div className="mt-6">
          <Button
            title={t('account/age/next_button')}
            disabled={!(countrySelectBox.isValid && birthInput.isValid)}
            onClick={onClick}
          ></Button>
        </div>
      </div>
      <ul className="container mt-10 py-6 N_12_R text-brown-grey bg-pale-grey list-disc">
        <li>{t('account/age/disclaimer_1')}</li>
        <li>{t('account/age/disclaimer_2')}</li>
      </ul>
    </div>
  );
}
